// src/pages/LivePublic.jsx
// Visualizzazione pubblica identica a RaceLive ma senza comandi.
// Quando NON c'è una gara attiva può mostrare la "Landing Page Gara"
// (configurabile da /landing-gara): countdown al prossimo evento +
// anteprima del circuito con cordoli.
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import RaceLive from './RaceLive';
import logoRaceSenseLive from '../img/logo-racesense-live.svg';
import { ThemeToggle } from '../components/ThemeContext';
import './LivePublic.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const WS_URL = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:'
  ? `wss://${window.location.hostname}/ws`
  : `ws://${window.location.hostname}:5001`);

/* ============================================================
   Countdown live verso una data ISO.
   Ritorna null se non c'è data valida, {done:true} se passata.
============================================================ */
function useCountdown(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (!iso) return null;
  const target = Date.parse(iso);
  if (!Number.isFinite(target)) return null;
  const diff = target - now;
  if (diff <= 0) return { done: true };
  const sec = Math.floor(diff / 1000);
  return {
    done: false,
    days: Math.floor(sec / 86400),
    hours: Math.floor((sec % 86400) / 3600),
    minutes: Math.floor((sec % 3600) / 60),
    seconds: sec % 60,
  };
}

function formatEventDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const s = new Intl.DateTimeFormat('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ============================================================
   Canvas statico del circuito: asfalto (bordi manuali o centerline),
   cordoli rosso/bianco, linea start/finish. Stessa resa del canvas
   di gara, senza zoom/pan. Sfondo scuro sempre (scelta grafica).
============================================================ */
function LandingCircuitCanvas({ circuit }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  const bounds = useMemo(() => {
    if (!circuit) return null;
    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    const add = (lat, lon) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon; if (lon > maxLon) maxLon = lon;
    };
    (circuit.sectors || []).forEach(s => add(s.lat, s.lon));
    (circuit.leftBorder || []).forEach(s => add(s.lat, s.lon));
    (circuit.rightBorder || []).forEach(s => add(s.lat, s.lon));
    (circuit.kerbs || []).forEach(k => (k.points || []).forEach(p => add(p.lat, p.lon)));
    if (!Number.isFinite(minLat)) return null;
    return { minLat, maxLat, minLon, maxLon };
  }, [circuit]);

  useEffect(() => {
    const canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap || !bounds) return undefined;
    const ctx = canvas.getContext('2d');

    const draw = () => {
      const cssW = wrap.clientWidth;
      if (!cssW) return;
      const cssH = Math.max(220, Math.min(Math.round(cssW * 0.56), 420));
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      canvas.style.height = cssH + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      const b = bounds;
      const centerLat = (b.minLat + b.maxLat) / 2;
      const cos = Math.cos(centerLat * Math.PI / 180) || 1;
      const worldW = Math.max((b.maxLon - b.minLon) * cos, 1e-6);
      const worldH = Math.max((b.maxLat - b.minLat), 1e-6);
      const pad = 30;
      const scale = Math.min((cssW - pad * 2) / worldW, (cssH - pad * 2) / worldH);
      const offX = (cssW - worldW * scale) / 2;
      const offY = (cssH - worldH * scale) / 2;
      const project = (lat, lon) => ({
        x: offX + ((lon - b.minLon) * cos) * scale,
        y: offY + (b.maxLat - lat) * scale,
      });

      const trackPx = Math.max(6, Math.min(26, cssW / 55));
      const left = circuit?.leftBorder, right = circuit?.rightBorder;
      const sectors = circuit?.sectors;
      const hasManualBorders = Array.isArray(left) && Array.isArray(right) && left.length > 1 && right.length > 1;
      const pts = Array.isArray(sectors) ? sectors.map(s => project(s.lat, s.lon)) : [];

      if (hasManualBorders) {
        ctx.beginPath();
        left.forEach((p, i) => { const q = project(p.lat, p.lon); i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y); });
        for (let i = right.length - 1; i >= 0; i--) { const q = project(right[i].lat, right[i].lon); ctx.lineTo(q.x, q.y); }
        ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,0.06)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.24)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      } else if (pts.length > 1) {
        ctx.beginPath();
        pts.forEach((q, i) => i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y));
        ctx.lineTo(pts[0].x, pts[0].y);
        ctx.strokeStyle = 'rgba(255,255,255,0.10)';
        ctx.lineWidth = trackPx + 3;
        ctx.lineJoin = 'round'; ctx.lineCap = 'round';
        ctx.stroke();
        ctx.strokeStyle = 'rgba(255,255,255,0.25)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Cordoli rosso/bianco alternati
      if (Array.isArray(circuit?.kerbs)) {
        const kw = Math.max(2.5, trackPx * 0.18);
        circuit.kerbs.forEach(k => {
          const kp = (k.points || []).map(p => project(p.lat, p.lon));
          if (kp.length < 2) return;
          const seq = k.closed ? [...kp, kp[0]] : kp;
          for (let i = 1; i < seq.length; i++) {
            ctx.beginPath();
            ctx.moveTo(seq[i - 1].x, seq[i - 1].y);
            ctx.lineTo(seq[i].x, seq[i].y);
            ctx.strokeStyle = i % 2 === 0 ? '#e10600' : '#ffffff';
            ctx.lineWidth = kw; ctx.lineCap = 'butt';
            ctx.stroke();
          }
        });
      }

      // Linea start/finish
      if (pts.length > 1) {
        const a = pts[0], c = pts[1];
        const dx = c.x - a.x, dy = c.y - a.y;
        const len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len, ny = dx / len;
        const half = Math.max(9, trackPx * 0.7);
        ctx.beginPath();
        ctx.moveTo(a.x - nx * half, a.y - ny * half);
        ctx.lineTo(a.x + nx * half, a.y + ny * half);
        ctx.strokeStyle = 'rgba(255,255,255,0.85)';
        ctx.lineWidth = Math.max(2.5, trackPx * 0.16);
        ctx.setLineDash([3.5, 3.5]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    };

    draw();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(draw) : null;
    if (ro) ro.observe(wrap);
    window.addEventListener('resize', draw);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', draw);
    };
  }, [bounds, circuit]);

  if (!bounds) return null;
  return (
    <div ref={wrapRef} className="live-landing-canvas-wrap">
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block' }} />
    </div>
  );
}

/* ============================================================
   Landing "prossimo evento": hero + countdown + circuito.
============================================================ */
function RaceLanding({ landing, wsConnected }) {
  const [circuit, setCircuit] = useState(null);
  const cd = useCountdown(landing.eventDate);
  const prettyDate = formatEventDate(landing.eventDate);

  // Geometria completa del circuito selezionato (cordoli inclusi)
  useEffect(() => {
    let alive = true;
    setCircuit(null);
    if (!landing.circuitId) return undefined;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/circuits/${encodeURIComponent(landing.circuitId)}`);
        if (r.ok) {
          const full = await r.json();
          if (alive) setCircuit(full);
        }
      } catch { /* no-op: la landing resta valida anche senza circuito */ }
    })();
    return () => { alive = false; };
  }, [landing.circuitId]);

  const lengthKm = circuit?.stats?.lengthMeters ? (circuit.stats.lengthMeters / 1000).toFixed(2) : null;
  const cornersCount = Array.isArray(circuit?.corners) && circuit.corners.length ? circuit.corners.length : null;

  return (
    <div className="live-landing rs-live-root">
      <div style={{ position: 'absolute', top: 18, right: 20, zIndex: 10 }}>
        <ThemeToggle />
      </div>
      <div className="live-public-bg-grid" />
      <div className="live-public-bg-glow" />

      <div className="live-landing-container">
        {/* Logo */}
        <div className="live-public-logo-wrapper">
          <img src={logoRaceSenseLive} alt="RaceSense Live" className="live-public-logo" />
        </div>

        {/* Hero evento */}
        <div className="live-landing-hero">
          <span className="live-landing-kicker">
            <span className="live-landing-kicker-line" />
            PROSSIMO EVENTO
            <span className="live-landing-kicker-line" />
          </span>
          <h1 className="live-landing-title">{landing.eventName || 'Prossima gara in arrivo'}</h1>
          {landing.eventSubtitle && <p className="live-landing-subtitle">{landing.eventSubtitle}</p>}

          {(prettyDate || landing.location) && (
            <div className="live-landing-meta">
              {prettyDate && (
                <span className="live-landing-meta-item">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                  {prettyDate}
                </span>
              )}
              {landing.location && (
                <span className="live-landing-meta-item">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                  {landing.location}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Countdown */}
        {cd && !cd.done && (
          <div className="live-landing-countdown" role="timer" aria-label="Countdown al prossimo evento">
            {[['GIORNI', cd.days], ['ORE', cd.hours], ['MINUTI', cd.minutes], ['SECONDI', cd.seconds]].map(([lab, val], i) => (
              <React.Fragment key={lab}>
                {i > 0 && <span className="live-landing-cd-sep">:</span>}
                <div className="live-landing-cd-tile">
                  <span className="live-landing-cd-num">{String(val).padStart(2, '0')}</span>
                  <span className="live-landing-cd-lab">{lab}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
        {cd?.done && (
          <div className="live-landing-imminent">
            <span className="live-landing-imminent-dot" />
            EVENTO IN PARTENZA — LA DIRETTA STA PER INIZIARE
          </div>
        )}

        {/* Circuito */}
        {circuit && (
          <div className="live-landing-track-card">
            <div className="live-landing-track-head">
              <div className="live-landing-track-name">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 12a8 8 0 1 1 16 0v3a3 3 0 0 1-3 3h-2a1 1 0 1 1 0-2h2a1 1 0 0 0 1-1v-3a6 6 0 1 0-12 0v3a1 1 0 0 0 1 1h2a1 1 0 1 1 0 2H7a3 3 0 0 1-3-3v-3z" /><circle cx="12" cy="12" r="2" /></svg>
                {circuit.name || 'Circuito'}
              </div>
              <div className="live-landing-track-stats">
                {lengthKm && <span><strong>{lengthKm}</strong> km</span>}
                {cornersCount && <span><strong>{cornersCount}</strong> curve</span>}
              </div>
            </div>
            <LandingCircuitCanvas circuit={circuit} />
          </div>
        )}

        {/* Stato connessione */}
        <div className="live-public-status">
          <div className={`live-public-status-dot ${wsConnected ? 'connected' : 'disconnected'}`} />
          <span>{wsConnected ? 'La diretta si avvierà automaticamente su questa pagina' : 'Riconnessione...'}</span>
        </div>
      </div>
    </div>
  );
}

export default function LivePublic() {
  const { raceId } = useParams();
  const [raceActive, setRaceActive] = useState(null); // null = loading, true/false = stato
  const [wsConnected, setWsConnected] = useState(false);
  const [landing, setLanding] = useState(null); // null = non ancora caricata

  // Config landing (pagina "prossimo evento"). Caricata subito e
  // aggiornata in tempo reale dal messaggio WS 'landing_updated'.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/landing`);
        const j = r.ok ? await r.json() : null;
        if (alive) setLanding(j?.landing || { enabled: false });
      } catch {
        if (alive) setLanding({ enabled: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  // Controlla se c'è una gara attiva via WebSocket, con RICONNESSIONE AUTOMATICA:
  // così la pagina pubblica/landing resta viva tra una gara e l'altra (cambio
  // gara, riavvio server, timeout idle) e passa da sola alla diretta quando parte
  // una nuova gara, senza dover ricaricare. Alla riconnessione il server reinvia
  // subito snapshot / race_inactive.
  useEffect(() => {
    let cancelled = false;
    let ws = null;
    let reconnectTimer = null;
    let timeout;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data?.type === 'race_snapshot') {
          clearTimeout(timeout);
          setRaceActive(true);
        }
        if (data?.type === 'race_inactive') {
          setRaceActive(false);
        }
        if (data?.type === 'landing_updated' && data.data) {
          setLanding(data.data);
        }
      } catch { /* ignore */ }
    };

    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(WS_URL + '?role=viewer');
      ws.onopen = () => {
        setWsConnected(true);
        // Se non riceviamo snapshot entro 3s, non c'è gara
        timeout = setTimeout(() => {
          if (raceActive === null) setRaceActive(false);
        }, 3000);
      };
      ws.onmessage = handleMessage;
      ws.onerror = () => setWsConnected(false);
      ws.onclose = () => {
        setWsConnected(false);
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, 1500);
      };
    };
    connect();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch { }
    };
  }, [raceActive]);

  // Loading state (attende anche la config landing per evitare flash di pagina sbagliata)
  if (raceActive === null || (raceActive === false && landing === null)) {
    return (
      <div className="live-public-loading rs-live-root">
        <div style={{ position: 'absolute', top: 18, right: 20, zIndex: 10 }}>
          <ThemeToggle />
        </div>
        <div className="live-public-loading-spinner" />
        <span>Connessione in corso...</span>
      </div>
    );
  }

  // No race active → landing personalizzata se configurata
  if (!raceActive) {
    if (landing?.enabled) {
      return <RaceLanding landing={landing} wsConnected={wsConnected} />;
    }
    return (
      <div className="live-public-no-race rs-live-root">
        <div style={{ position: 'absolute', top: 18, right: 20, zIndex: 10 }}>
          <ThemeToggle />
        </div>
        {/* Background effects */}
        <div className="live-public-bg-grid" />
        <div className="live-public-bg-glow" />

        <div className="live-public-no-race-container">
          {/* Logo */}
          <div className="live-public-logo-wrapper">
            <img
              src={logoRaceSenseLive}
              alt="RaceSense Live"
              className="live-public-logo"
            />
          </div>

          {/* Card principale */}
          <div className="live-public-no-race-card">
            <div className="live-public-no-race-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
            </div>
            <h1 className="live-public-no-race-title">
              Nessuna gara in corso
            </h1>
            <p className="live-public-no-race-text">
              Al momento non ci sono sessioni live attive.
              <br />
              Torna più tardi per seguire la prossima gara in tempo reale.
            </p>

            {/* Status indicator */}
            <div className="live-public-status">
              <div className={`live-public-status-dot ${wsConnected ? 'connected' : 'disconnected'}`} />
              <span>
                {wsConnected ? 'In attesa di una sessione...' : 'Riconnessione...'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Race active - mostra la classifica live
  return <RaceLive publicMode={true} raceId={raceId} />;
}
